import * as React from 'react';
import { equals } from 'rambda';
import styled from 'styled-components';
import { AdDimension, debounce } from '@packages/common';

import { initGooglePublisherTag, getId, getDimensions } from './utils';

const enableSingleRequest = true;
const responsive = true;

const Container = styled.div`
  overflow: hidden;
  text-align: center;

  > div {
    display: inline-block;
  }

  iframe {
    /* max-with needed for offsetWidth returning the right size for filled slot */
    max-width: 100%;
  }
`;

export interface GooglePublisherTagProps {
  className?: string;
  id?: string;
  path: string;
  dimensions: AdDimension[];
  responsive?: boolean;
  enableSingleRequest?: boolean;
  loadImmediately?: boolean;
  onSlotRenderEnded?: () => void;
  onSlotVisibilityChanged?: () => void;
  onImpressionViewable?: () => void;
}

export class GooglePublisherTag extends React.Component<GooglePublisherTagProps> {
  private googletag: any;
  private slot: any;
  private slotDimensions?: AdDimension[];
  readonly node: React.RefObject<HTMLDivElement>;
  readonly handleResizeDebounced: EventListener;
  readonly handleScrollDebounced: EventListener;

  constructor(props: GooglePublisherTagProps) {
    super(props);
    this.node = React.createRef();
    this.handleResizeDebounced = debounce(this.handleResize, 100);
    this.handleScrollDebounced = debounce(this.handleScroll, 100);
  }

  componentDidMount() {
    const googletag: any = initGooglePublisherTag({ ...this.props, enableSingleRequest, responsive });

    window.addEventListener('resize', this.handleResizeDebounced);

    if (googletag) {
      googletag.cmd.push(() => {
        this.googletag = googletag;
        this.update(this.props);
      });
    }
  }

  UNSAFE_componentWillReceiveProps(props: GooglePublisherTagProps) {
    this.update(props);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResizeDebounced);
    this.removeSlot();
  }

  update(props: GooglePublisherTagProps) {
    if (!this.googletag) {
      return;
    }

    const { node } = this;
    if (!node.current) {
      return;
    }

    const nodeWidth: number = node.current.offsetWidth;
    // filter by available node space
    const dimensions = getDimensions(props, nodeWidth);

    // dimensions changed
    if (!equals(dimensions, this.slotDimensions)) {
      this.removeSlot();
      this.slotDimensions = dimensions;

      if (dimensions && dimensions.length) {
        const { path } = this.props;
        const id = getId(this.props);
        node.current.innerHTML = `<div id="${id}"></div>`;

        const slot = this.googletag.defineSlot(path, dimensions, id);
        if (slot) {
          slot.addService(this.googletag.pubads());
          this.slot = slot;
        }

        window.addEventListener('scroll', this.handleScrollDebounced);
        this.handleScroll();
      }
    }
  }

  displaySlot = () => {
    const slot = this.slot;
    if (slot) {
      this.googletag.display(slot.getSlotElementId());
      this.googletag.pubads().refresh([slot]);
    }
  };

  removeSlot() {
    window.removeEventListener('scroll', this.handleScrollDebounced);
    if (!this.slot) {
      return;
    }

    if (this.googletag) {
      this.googletag.destroySlots([this.slot]);
    }

    this.slot = null;
  }

  handleResize = () => {
    this.update(this.props);
  };

  handleScroll = () => {
    const nodeCurrent = this.node && this.node.current;
    if (nodeCurrent && (nodeCurrent.offsetTop - window.innerHeight <= window.scrollY || this.props.loadImmediately)) {
      this.displaySlot();
      window.removeEventListener('scroll', this.handleScrollDebounced);
    }
  };

  render() {
    const { className } = this.props;
    return <Container className={className} ref={this.node} />;
  }
}
